import { Router } from "src/components/Router";
import { QueryClientProvider, QueryClient, type QueryClientConfig } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import * as Sentry from "@sentry/react";
import { ApiContext } from "./contexts/api.context";
import { observer } from "mobx-react-lite";
import { Toaster, ToasterContext, useToaster } from "./contexts/toaster.context";
import { ToastFC } from "./components/Toast";
import { createPortal } from "react-dom";
import { Api } from "./types/Api";
import { AuthProvider } from "./contexts/auth.context";
import { EnvProvider } from "./contexts/env.context";
import { PosthogProvider } from "./contexts/posthog.context";
import { StripeProvider } from "./contexts/stripe.context";

const DefaultQueryClientConfig: QueryClientConfig = {
  defaultOptions: { queries: { refetchOnWindowFocus: false } },
};

export const App = observer(() => {
  const queryClient = useMemo(() => new QueryClient(DefaultQueryClientConfig), []);
  const [api] = useState(() => new Api());
  const [toaster] = useState(() => new Toaster());

  return (
    <div className="App" data-sl="canvas-hq">
      <ApiContext.Provider value={api}>
        <Sentry.ErrorBoundary>
          <QueryClientProvider client={queryClient}>
            <EnvProvider>
              <PosthogProvider>
                <AuthProvider>
                  <StripeProvider>
                    <ToasterContext.Provider value={toaster}>
                      <ToasterFC />
                      <Router />
                    </ToasterContext.Provider>
                  </StripeProvider>
                </AuthProvider>
              </PosthogProvider>
            </EnvProvider>
          </QueryClientProvider>
        </Sentry.ErrorBoundary>
      </ApiContext.Provider>
    </div>
  );
});

const ToasterFC = observer(() => {
  const toaster = useToaster();
  const [root, setRoot] = useState<null | HTMLDivElement>(null);

  useEffect(() => {
    const root = document.createElement("div");
    document.body.appendChild(root);
    setRoot(root);
    return () => {
      document.body.removeChild(root);
    };
  }, []);

  if (!root) return null;
  return createPortal(
    <div>
      {toaster.toasts.map((toast) => (
        <ToastFC key={toast.id} toast={toast} />
      ))}
    </div>,
    root
  );
});
